import React, { useEffect, useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote, Facebook, Search, Sparkles } from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

function SourceBadge({ source }) {
  if (source === 'google') {
    return (
      <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 text-black text-[9px] tracking-[0.2em] font-bold">
        <Search className="w-3 h-3" /> GOOGLE
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-600 text-white text-[9px] tracking-[0.2em] font-bold">
      <Facebook className="w-3 h-3" /> FACEBOOK
    </span>
  );
}

// Pure presentational card. Position/opacity/blur driven imperatively via DOM ref by parent rAF loop.
const ReviewCard = React.forwardRef(function ReviewCard({ review, isActive, onClick }, ref) {
  return (
    <div
      ref={ref}
      onClick={onClick}
      className="absolute top-1/2 left-1/2 cursor-pointer will-change-transform"
      style={{ width: 480, transform: 'translate(-50%, -50%)' }}
      data-cursor="hover"
    >
      <div className="relative">
        {isActive && (
          <div className="absolute -inset-2 rounded-3xl bg-gradient-to-r from-red-600/40 via-red-500/20 to-red-600/40 blur-xl animate-glow" />
        )}
        <div className={`relative rounded-3xl p-8 backdrop-blur-md border-2 ${isActive ? 'bg-zinc-950/95 border-red-600/60 shadow-2xl shadow-red-600/30' : 'bg-zinc-950/85 border-zinc-900'}`}>
          <Quote className={`absolute top-5 right-5 w-12 h-12 ${isActive ? 'text-red-600/30' : 'text-zinc-800/60'}`} fill="currentColor" />
          <div className="flex items-center justify-between mb-5">
            <SourceBadge source={review.source} />
            <div className="flex gap-0.5">
              {Array.from({ length: review.rating }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-red-500 text-red-500" />
              ))}
            </div>
          </div>
          <p className={`font-heading text-lg md:text-xl leading-snug min-h-[120px] ${isActive ? 'text-white' : 'text-zinc-300'}`}>
            <span className="text-red-500 text-2xl mr-1">&ldquo;</span>
            {review.quote}
            <span className="text-red-500 text-2xl ml-1">&rdquo;</span>
          </p>
          <div className="mt-6 pt-5 border-t border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-11 h-11 rounded-full flex items-center justify-center font-display text-lg border ${isActive ? 'bg-gradient-to-br from-red-600 to-red-700 border-red-400 text-white' : 'bg-zinc-900 border-zinc-800 text-zinc-300'}`}>
                {review.name.split(' ').map((n) => n[0]).slice(0, 2).join('')}
              </div>
              <div>
                <div className="font-display text-lg tracking-wide leading-tight">{review.name}</div>
                <div className="text-[10px] tracking-[0.2em] text-zinc-500">{review.role}</div>
              </div>
            </div>
            {isActive && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-600/15 border border-red-600/40 text-[9px] tracking-[0.2em] font-bold text-red-400">
                <Sparkles className="w-3 h-3" /> VERIFIED
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

function FloatingStat({ icon: Icon, label, value, delay = 0 }) {
  return (
    <div
      className="px-4 py-3 bg-black/85 backdrop-blur-md border border-red-600/40 rounded-xl shadow-lg shadow-red-600/10 animate-float-slow"
      style={{ animationDelay: `${delay}s` }}
    >
      <div className="flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-red-500" />
        <div>
          <div className="text-[9px] tracking-[0.3em] text-zinc-500 font-bold">{label}</div>
          <div className="font-display text-lg text-white leading-none mt-0.5">{value}</div>
        </div>
      </div>
    </div>
  );
}

const SLOTS = 7;     // visible card slots (-3..+3)
const HALF = 3;

export default function Testimonials() {
  const { content } = useSiteContent();
  const testimonials = content.testimonials || [];
  const reviewStats = content.reviewStats || { rating: 0, total: 0, fiveStar: 0 };
  const N = testimonials.length;
  // baseIdx = integer "anchor" for content. Updates only when crossed (cheap re-render).
  const [baseIdx, setBaseIdx] = useState(0);
  const baseIdxRef = useRef(0);
  // Continuous fractional position (rAF-driven). Drift target moves continuously L→R.
  const posRef = useRef(0);
  const targetDeltaRef = useRef(0); // user-jump delta accumulated (eased toward)
  const pausedRef = useRef(false);
  const [paused, setPaused] = useState(false);
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    let raf;
    let last = performance.now();
    let inView = true;
    const driftSpeed = 0.28; // cards per second — smooth glide
    const ease = 7;          // user-jump easing speed

    const tick = (now) => {
      const dt = Math.min(64, now - last) / 1000;
      last = now;

      // Continuous drift L→R when not paused
      if (!pausedRef.current) {
        posRef.current -= driftSpeed * dt;
      }
      // Ease user-click jumps toward zero (so posRef catches up to user target)
      if (Math.abs(targetDeltaRef.current) > 0.001) {
        const f = 1 - Math.pow(0.001, dt * ease / 6);
        const step = targetDeltaRef.current * f;
        posRef.current += step;
        targetDeltaRef.current -= step;
      }

      const pos = posRef.current;
      const newBaseIdx = Math.round(pos);

      // Imperatively update each visible slot's transform/opacity/filter — NO React re-render
      for (let s = 0; s < SLOTS; s++) {
        const node = cardRefs.current[s];
        if (!node) continue;
        const d = s - HALF; // -3..+3
        const distance = (newBaseIdx + d) - pos;
        const absD = Math.abs(distance);
        const offsetX = distance * 260;
        const rotateY = -distance * 22;
        const z = -absD * 240;
        const focusBoost = Math.max(0, 1 - absD * 4) * 0.04;
        const scale = Math.max(0.74, 1 - absD * 0.07) + focusBoost;
        const opacity = Math.max(0, 1 - absD * 0.32);
        const blur = Math.min(6, absD * 2.0);
        node.style.transform = `translate3d(-50%, -50%, 0) translate3d(${offsetX}px, 0, ${z}px) rotateY(${rotateY}deg) scale(${scale})`;
        node.style.opacity = opacity;
        node.style.filter = blur > 0.05 ? `blur(${blur}px)` : 'none';
        node.style.zIndex = 100 - Math.round(absD * 10);
        node.style.pointerEvents = absD < 2.5 ? 'auto' : 'none';
      }

      // Only when integer crosses do we update React content (every ~3.5s with drift 0.28)
      if (newBaseIdx !== baseIdxRef.current) {
        baseIdxRef.current = newBaseIdx;
        setBaseIdx(newBaseIdx);
      }

      // Wrap to keep numbers small
      if (posRef.current < -N * 1000) {
        posRef.current += N * 1000;
        baseIdxRef.current += N * 1000;
        setBaseIdx(baseIdxRef.current);
      }

      if (inView) raf = requestAnimationFrame(tick);
      else raf = 0;
    };
    raf = requestAnimationFrame(tick);

    // Pause the carousel's RAF when the section scrolls offscreen — no
    // reason to burn ~60 transform updates / sec on cards the user can't see.
    let io;
    const sec = sectionRef.current;
    if (sec && typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver(([entry]) => {
        const next = entry.isIntersecting;
        if (next === inView) return;
        inView = next;
        if (inView && !raf) {
          last = performance.now();
          raf = requestAnimationFrame(tick);
        }
      }, { rootMargin: '100px' });
      io.observe(sec);
    }
    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (io) io.disconnect();
    };
  }, [N]);

  const next = () => { targetDeltaRef.current += -1; };
  const prev = () => { targetDeltaRef.current += 1; };
  const jumpTo = (i) => {
    const curMod = ((baseIdxRef.current % N) + N) % N;
    let delta = i - curMod;
    if (delta > N / 2) delta -= N;
    if (delta < -N / 2) delta += N;
    targetDeltaRef.current += delta;
  };

  // Mouse spotlight — RAF-coalesced + cached rect + coarse-pointer guard.
  // Replaces the previous on-every-mousemove `getBoundingClientRect()` (which
  // forced a sync layout each pointer pixel and made the carousel feel heavy).
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(pointer: coarse)').matches) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const el = sectionRef.current;
    if (!el) return undefined;
    let rect = el.getBoundingClientRect();
    const cacheRect = () => { rect = el.getBoundingClientRect(); };
    let raf = 0;
    let lx = 0;
    let ly = 0;
    const onMove = (e) => {
      lx = e.clientX;
      ly = e.clientY;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        el.style.setProperty('--mx', `${lx - rect.left}px`);
        el.style.setProperty('--my', `${ly - rect.top}px`);
      });
    };
    el.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('scroll', cacheRect, { passive: true });
    window.addEventListener('resize', cacheRect, { passive: true });
    return () => {
      el.removeEventListener('pointermove', onMove);
      window.removeEventListener('scroll', cacheRect);
      window.removeEventListener('resize', cacheRect);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Build the 7 visible slots (content only — positions are set imperatively)
  const slots = [];
  for (let s = 0; s < SLOTS; s++) {
    const d = s - HALF;
    const i = ((baseIdx + d) % N + N) % N;
    slots.push({ slot: s, review: testimonials[i], idx: i, isActive: d === 0 });
  }

  const activeIdx = ((baseIdx % N) + N) % N;

  return (
    <section
      id="reviews"
      ref={sectionRef}
      data-testid="testimonials-section"
      className="spotlight relative py-16 sm:py-24 lg:py-32 bg-black overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-20" />
      <div className="absolute inset-0 diagonal-stripes opacity-25" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-1/2 bg-red-600/15 blur-[90px] rounded-full pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 text-center reveal">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="h-[2px] w-10 bg-red-600" />
          <span className="text-xs tracking-[0.4em] text-red-500 font-semibold">MEMBER REVIEWS</span>
          <div className="h-[2px] w-10 bg-red-600" />
        </div>
        <h2 className="font-display text-5xl md:text-7xl leading-[0.9] tracking-tight">
          REAL MEMBERS. <span className="text-gradient-red">REAL RESULTS.</span>
        </h2>
        <p className="mt-5 text-zinc-400 max-w-xl mx-auto">
          {reviewStats.total}+ verified reviews from real members on Google &amp; Facebook.
        </p>

        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-4 py-2 bg-zinc-950 border border-red-600/30 rounded-full">
            <div className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-red-500 text-red-500" />
              ))}
            </div>
            <span className="font-display text-lg text-white">{reviewStats.rating}</span>
            <span className="text-[10px] tracking-[0.2em] text-zinc-500 font-bold">AVG RATING</span>
          </div>
          <div className="px-4 py-2 bg-zinc-950 border border-zinc-800 rounded-full text-[11px] tracking-[0.2em] text-zinc-300">
            <span className="font-display text-lg text-red-500 mr-1.5">{reviewStats.total}+</span> REVIEWS
          </div>
          <div className="px-4 py-2 bg-zinc-950 border border-zinc-800 rounded-full text-[11px] tracking-[0.2em] text-zinc-300">
            <span className="font-display text-lg text-red-500 mr-1.5">{reviewStats.fiveStar}</span> 5-STAR
          </div>
        </div>
      </div>

      {/* 3D Coverflow Stage */}
      <div
        className="relative mx-auto mt-16"
        style={{ perspective: '1500px', height: 380 }}
        onMouseEnter={() => { setPaused(true); pausedRef.current = true; }}
        onMouseLeave={() => { setPaused(false); pausedRef.current = false; }}
      >
        <div className="relative w-full h-full" style={{ transformStyle: 'preserve-3d' }}>
          {slots.map((v) => (
            <ReviewCard
              key={v.slot}
              ref={(el) => (cardRefs.current[v.slot] = el)}
              review={v.review}
              isActive={v.isActive}
              onClick={() => jumpTo(v.idx)}
            />
          ))}
        </div>
      </div>

      <div className="relative max-w-5xl mx-auto px-6 mt-10 flex items-center justify-center gap-4">
        <button onClick={prev} data-cursor="hover" className="w-12 h-12 rounded-full border border-zinc-800 hover:border-red-500 hover:bg-red-600/10 flex items-center justify-center transition group" aria-label="Previous review">
          <ChevronLeft className="w-5 h-5 group-hover:-translate-x-0.5 transition" />
        </button>

        <div className="flex items-center gap-1.5 px-2">
          {testimonials.map((t, i) => (
            <button
              key={t.id || t.quote}
              onClick={() => jumpTo(i)}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === activeIdx ? 'w-8 bg-red-500' : 'w-1.5 bg-zinc-700 hover:bg-zinc-500'}`}
              aria-label={`Go to review ${i + 1}`}
            />
          ))}
        </div>

        <button onClick={next} data-cursor="hover" className="w-12 h-12 rounded-full border border-zinc-800 hover:border-red-500 hover:bg-red-600/10 flex items-center justify-center transition group" aria-label="Next review">
          <ChevronRight className="w-5 h-5 group-hover:translate-x-0.5 transition" />
        </button>
      </div>

      <div className="hidden lg:block absolute top-32 right-12 z-10">
        <FloatingStat icon={Star} label="GOOGLE" value="91 reviews" delay={0} />
      </div>
      <div className="hidden lg:block absolute bottom-32 left-12 z-10">
        <FloatingStat icon={Facebook} label="FACEBOOK" value="129 reviews" delay={0.6} />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 mt-6 text-center">
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] text-zinc-600 font-bold">
          <span className="relative flex h-1.5 w-1.5">
            {!paused && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />}
            <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${paused ? 'bg-zinc-600' : 'bg-red-500'}`} />
          </span>
          <span className="hide-on-touch">
            {paused ? 'PAUSED · MOVE AWAY TO RESUME' : 'AUTO PLAYING · HOVER TO PAUSE'}
          </span>
          <span className="show-on-touch">REAL REVIEWS · 5.0 ★</span>
        </div>
      </div>
    </section>
  );
}
